// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Link,
  Button,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { AppearanceDialog } from './AppearancePanel';

export const Header = ({
  onOpenMobileSidebar,
  company,
  isImpersonating,
  onReturnToSuperAdmin,
  userRole,
  contentMaxWidth = '56rem', // Default for index/chat page, use '1400px' for admin pages
  contentPadding = '15px', // Default for index/chat page, use '24px' for admin pages
  logoMinWidth = '1500px', // Min viewport width to show logo (1500px for index, 1700px for admin pages)
  // Share feature props
  currentThreadId,
  threadSharedWith, // Array of user objects this thread is shared with (null if not shared)
  isThreadOwner, // true if current user owns the thread
  onShareClick, // Callback when share button is clicked
  isIncognitoChat, // true when the current chat is incognito (no sharing, no memory)
  onIncognitoClick, // clicking the incognito chip exits incognito (new regular chat)
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const theme = useTheme();

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Check if user can access Company Admin (admins and owners only)
  const canAccessCompanyAdmin = userRole === 'super_admin' ||
                                 userRole === 'company_admin' ||
                                 userRole === 'company_owner';

  const navItems = [
    { label: 'Home', href: '/', icon: <HomeIcon /> },
    { label: 'Manage Assistants', href: '/manage-assistants.html', icon: <SmartToyIcon /> },
    ...(canAccessCompanyAdmin ? [{ label: 'Company Admin', href: '/company-admin.html', icon: <SettingsIcon /> }] : []),
  ];

  return (
    <>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar
          sx={{
            minHeight: 64,
            px: { xs: 1, sm: 2 },
            position: 'relative',
          }}
        >
          {/* Left side - Logo and mobile menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 1 }}>
            {/* Mobile Menu Button - for chat sidebar */}
            {onOpenMobileSidebar && (
              <IconButton
                onClick={onOpenMobileSidebar}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  mr: 1,
                  color: 'text.secondary',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Mobile Navigation Menu Button */}
            {!onOpenMobileSidebar && (
              <IconButton
                onClick={handleMobileMenuToggle}
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  mr: 1,
                  color: 'text.secondary',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Company Logo - always visible on mobile, hidden on tablet/desktop until logoMinWidth */}
            {company?.logo_url && (
              <Link
                href="/"
                sx={{
                  display: 'flex', // Show on mobile
                  alignItems: 'center',
                  minWidth: 0,
                  '@media (min-width: 600px)': {
                    display: 'none', // Hide on tablet/desktop
                  },
                  [`@media (min-width: ${logoMinWidth})`]: {
                    display: 'flex', // Show again on large screens
                  },
                }}
              >
                <Box
                  component="img"
                  src={(theme.palette.mode === 'dark' && company.logo_dark_url) || company.logo_url}
                  alt={`${company.name} logo`}
                  sx={{
                    maxHeight: 48,
                    maxWidth: { xs: 'calc(100vw - 120px)', sm: 300 },
                    objectFit: 'contain',
                  }}
                />
              </Link>
            )}
            {!company?.logo_url && company?.name && (
              <Link
                href="/"
                underline="none"
                sx={{
                  display: 'block', // Show on mobile
                  color: 'text.primary',
                  '&:hover': { color: 'text.secondary' },
                  '@media (min-width: 600px)': {
                    display: 'none', // Hide on tablet/desktop
                  },
                  [`@media (min-width: ${logoMinWidth})`]: {
                    display: 'block', // Show again on large screens
                  },
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {company.name}
                </Typography>
              </Link>
            )}
          </Box>

          {/* Center - Menu container aligned with page content */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: contentMaxWidth,
                display: 'flex',
                alignItems: 'center',
                px: contentPadding,
                pointerEvents: 'auto',
              }}
            >
              <Box sx={{ display: onOpenMobileSidebar ? { xs: 'none', md: 'flex' } : { xs: 'none', sm: 'flex' }, gap: 1, alignItems: 'center' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    href={item.href}
                    startIcon={item.icon}
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}

                {/* Incognito indicator — click to exit incognito (new regular chat) */}
                {isIncognitoChat && (
                  <Tooltip title="Incognito chat: hidden from history and excluded from memory. Click to exit and start a new regular chat.">
                    <Box
                  component={onIncognitoClick ? 'button' : 'div'}
                  onClick={onIncognitoClick || undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: '#7c3aed',
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        ml: 1,
                        boxShadow: '0 1px 4px rgba(124, 58, 237, 0.4)',
                        border: 'none',
                        cursor: onIncognitoClick ? 'pointer' : 'default',
                        fontFamily: 'inherit',
                        transition: 'background-color .15s ease',
                        '&:hover': onIncognitoClick ? {
                          bgcolor: '#6d28d9',
                        } : undefined,
                      }}
                    >
                      <VisibilityOffIcon sx={{ fontSize: 18 }} />
                      Incognito
                    </Box>
                  </Tooltip>
                )}

                {/* Share Button - only show when a thread is selected (never for incognito chats) */}
                {currentThreadId && onShareClick && !isIncognitoChat && (
                  <Button
                    onClick={() => onShareClick()}
                    startIcon={threadSharedWith && threadSharedWith.length > 0 ? <PeopleIcon /> : <ShareIcon />}
                    sx={{
                      color: threadSharedWith && threadSharedWith.length > 0 ? '#fff' : '#f97316',
                      bgcolor: threadSharedWith && threadSharedWith.length > 0 ? '#f97316' : 'transparent',
                      border: '1px solid #f97316',
                      '&:hover': {
                        bgcolor: threadSharedWith && threadSharedWith.length > 0 ? '#ea580c' : 'rgba(249, 115, 22, 0.1)',
                      },
                      ml: 1,
                    }}
                  >
                    {threadSharedWith && threadSharedWith.length > 0
                      ? `Shared (${threadSharedWith.length})`
                      : 'Share'
                    }
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Right side - Appearance and Return */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, zIndex: 1 }}>
            {/* Appearance (mode, style, colors) - desktop only */}
            <Tooltip title="Appearance">
              <IconButton
                onClick={() => setAppearanceOpen(true)}
                sx={{
                  color: 'text.secondary',
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                <PaletteOutlinedIcon />
              </IconButton>
            </Tooltip>

            {/* Return to Super Admin Button */}
            {isImpersonating && (
              <Button
                onClick={onReturnToSuperAdmin}
                variant="contained"
                size="small"
                startIcon={<ExitToAppIcon />}
                sx={{
                  bgcolor: '#9333ea',
                  '&:hover': { bgcolor: '#7e22ce' },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Return
                </Box>
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navItems.map((item) => (
            <ListItem key={item.href} disablePadding>
              <ListItemButton
                component="a"
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
          {/* Share option in mobile menu */}
          {currentThreadId && onShareClick && !isIncognitoChat && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onShareClick();
                  setMobileMenuOpen(false);
                }}
                sx={{
                  color: threadSharedWith && threadSharedWith.length > 0 ? '#f97316' : 'text.primary',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: '#f97316' }}>
                  {threadSharedWith && threadSharedWith.length > 0 ? <PeopleIcon /> : <ShareIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={threadSharedWith && threadSharedWith.length > 0
                    ? `Shared (${threadSharedWith.length})`
                    : 'Share'
                  }
                />
              </ListItemButton>
            </ListItem>
          )}
        </List>
        <Divider />
        {/* Appearance in Mobile Menu */}
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setAppearanceOpen(true);
                setMobileMenuOpen(false);
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PaletteOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary="Appearance" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <AppearanceDialog open={appearanceOpen} onClose={() => setAppearanceOpen(false)} />
    </>
  );
};
